'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ExportSquare, MessageText1 } from 'iconsax-react';
import { Star } from 'lucide-react';
import {
    BlueCheck,
    BronzeBadge,
    GoldBadge,
    GoldCheck,
    RankedBadge,
    SilverBadge,
} from '@/components/Icons';
import { Exchange } from '@/types/exchanges/page';
import { toPersianDigits } from '@/utils/persianUtils';

interface ExchangesListCardProps {
    brokers?: Exchange[];
    onStarClick?: (broker: Exchange) => void | undefined;
    lastModifiedTitle?: string;
    currentPage?: number;
    isRanked?: boolean;
    excludeItem?: Exchange[] | undefined;
    isLanding?: boolean;
    // Accepted for older callers; badge ids are now unique on their own (see Icons.tsx).
    idPath?: string;
    visibleCount?: number;
    useCssHiding?: boolean;
    adsCount?: number;
}

export const getBadge = (
    number: number,
    isRank: boolean = false,
    size?: string,
): React.JSX.Element => {
    const adjustedNumber = isRank ? number - 1 : number;
    switch (adjustedNumber) {
        case 0:
            return <GoldBadge className={size ? size : 'size-8'} />;
        case 1:
            return <SilverBadge className={size ? size : 'size-8'} />;
        case 2:
            return <BronzeBadge className={size ? size : 'size-8'} />;
        default:
            return (
                <div className='text-base text-primary/70 dark:text-white/70 font-light'>
                    {isRank ? number : adjustedNumber + 1}
                </div>
            );
    }
};

const ExchangesListCard: React.FC<ExchangesListCardProps> = ({
    brokers,
    onStarClick,
    lastModifiedTitle,
    currentPage = 1,
    isRanked,
    excludeItem,
    isLanding,
    visibleCount,
    useCssHiding = false,
    adsCount = 0,
}) => {
    const getPaginatedRank = (index: number): number => {
        const adjustedIndex = index - adsCount;
        return (currentPage - 1) * 10 + adjustedIndex + 1;
    };

    const filteredBrokers = brokers?.filter(
        (broker) => !excludeItem?.some((excluded) => excluded.databaseId === broker.databaseId),
    );

    return (
        <ul className='grid grid-cols-1 lg:justify-center'>
            {filteredBrokers?.map((broker, index) => {
                // Determine visibility based on the count prop
                const isHidden =
                    useCssHiding && visibleCount !== undefined && index >= visibleCount;

                // --- LOGIC: Identify if this item is an Ad ---
                const isAd = index < adsCount;

                // --- LOGIC: Badge Content ---
                let badgeContent: React.ReactNode;

                if (isAd) {
                    badgeContent = (
                        <div className='flex flex-col items-center justify-center gap-1'>
                            <div className='w-6 h-6 text-yellow-500'>
                                <RankedBadge className='size-8' />
                            </div>
                        </div>
                    );
                } else {
                    badgeContent = isRanked ? (
                        <RankedBadge className='size-8' />
                    ) : (
                        getBadge(getPaginatedRank(index), true)
                    );
                }

                const {
                    faName,
                    enName,
                    logo,
                    ctaButton,
                    ctaId,
                    moreButtonId,
                    sponsored,
                    verified,
                    badge,
                } = broker?.brokerSingle?.newfields;
                const href = isLanding ? ctaButton?.url || broker?.uri || '#' : broker?.uri || '#';

                const finalRating =
                    lastModifiedTitle === 'broker'
                        ? broker?.brokerFields?.generalInfo?.brokerRating?.finalRating
                        : lastModifiedTitle === 'gold'
                          ? broker?.goldFields?.generalInfo?.brokerRating?.finalRating
                          : broker?.exchangeFields?.generalInfo?.brokerRating?.finalRating || 0;

                return (
                    <li
                        key={broker.databaseId}
                        // SEO FIX: We render the full content, but toggle the class between 'flex' and 'hidden'.
                        // 'hidden' applies display:none, but the HTML nodes remain in the source for Google to crawl.
                        className={`relative justify-between hover:bg-primary/5 dark:hover:bg-white/5 overflow-hidden border-b p-1 border-primary/10 dark:border-white/20 ${
                            isHidden ? 'hidden' : 'flex'
                        }`}
                    >
                        <div className='w-16 flex justify-center text-2xl'>
                            <div className='flex flex-col justify-center items-center gap-3'>
                                {badgeContent}
                            </div>
                        </div>

                        <div className='w-full border-r border-primary/10 dark:border-white/20 lg:pe-3 ps-3 flex flex-col lg:flex-row justify-center lg:justify-between text-white lg:items-center max-lg:gap-1 max-lg:py-1 gap-3'>
                            <Link
                                href={href}
                                id={moreButtonId || ''}
                                target={'_blank'}
                                className='flex gap-4 items-center'
                            >
                                {logo?.mediaItemUrl ? (
                                    <Image
                                        src={logo?.mediaItemUrl}
                                        alt={enName}
                                        className='object-contain size-8'
                                        loading={index < 4 ? 'eager' : 'lazy'}
                                        priority={index < 4}
                                        title={enName}
                                        width={100}
                                        height={100}
                                    />
                                ) : (
                                    // Fallback UI if logo is missing (Optional: You can use a placeholder icon here)
                                    <div className='size-8 rounded-none bg-gray-100 flex items-center justify-center text-xs text-gray-400'>
                                        Logo
                                    </div>
                                )}
                                <div className='flex flex-col items-start dark:text-white lg:text-xl text-primary'>
                                    <div className='inline-flex items-center gap-2  max-lg:gap-1 w-full'>
                                        <span className='max-md:text-sm text-base whitespace-nowrap'>
                                            {faName}
                                        </span>
                                        <span className='text-xs dark:text-white/80 max-lg:text-xxs line-clamp-1'>
                                            ({enName})
                                        </span>
                                        {sponsored ? (
                                            <div className='w-4'>
                                                <GoldCheck className='w-full h-full' />
                                            </div>
                                        ) : verified || badge ? (
                                            <div className='w-4'>
                                                <BlueCheck className='w-full h-full' />
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className='flex items-center gap-2 text-primary/70 dark:text-white/70'>
                                        <div className='text-xs flex gap-1 items-center'>
                                            <MessageText1
                                                size='18'
                                                className='stroke-primary/40 dark:stroke-white'
                                            />
                                            {toPersianDigits(broker?.postRatings?.count)}
                                            <span>نظر</span>
                                        </div>
                                        .
                                        <div
                                            className='flex items-center cursor-pointer gap-1'
                                            onClick={() => onStarClick?.(broker)}
                                        >
                                            <div className='relative w-[18px] h-[18px]'>
                                                <div
                                                    className='absolute top-0 left-0 right-0 w-full h-full overflow-hidden'
                                                    style={{
                                                        width: `${(Number(finalRating || 0) / 5) * 100}%`,
                                                    }}
                                                >
                                                    <Star
                                                        className='fill-yellow-500 stroke-yellow-500'
                                                        strokeWidth={1.5}
                                                        size={20}
                                                    />
                                                </div>
                                                <div className='absolute top-0 left-0 w-full h-full'>
                                                    <Star
                                                        className='stroke-yellow-500'
                                                        strokeWidth={1.5}
                                                        size={20}
                                                    />
                                                </div>
                                            </div>
                                            <span className='text-xs'>
                                                {toPersianDigits(finalRating)} از ۵ امتیاز
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>

                            <Link
                                href={ctaButton?.url || broker?.uri || '#'}
                                target='_blank'
                                rel='nofollow'
                                id={ctaId || ''}
                                className='bg-progress hover:brightness-110 justify-center max-lg:py-3 text-center flex text-nowrap items-center gap-2 p-2 text-xs lg:px-3 lg:rounded-xl rounded-2xl'
                            >
                                <ExportSquare className='stroke-white stroke-2' size={15} />
                                {ctaButton?.title || 'افتتاح حساب'}
                            </Link>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default ExchangesListCard;
